import React, { useState, useEffect } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import { roomService } from '../../../services/roomService';
import RoomTypeModal from './RoomTypeModal';
import ActionToast from '../../../components/ui/ActionToast';

const RoomTypeList = () => {
    const [roomTypes, setRoomTypes] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [notice, setNotice] = useState(null);
    const [showModal, setShowModal] = useState(false);
    const [selectedRoomType, setSelectedRoomType] = useState(null);

    useEffect(() => {
        fetchRoomTypes();
    }, []);

    const fetchRoomTypes = async () => {
        setLoading(true);
        setError(null);
        try {
            const types = await roomService.getRoomTypes();
            setRoomTypes(types);
        } catch (err) {
            console.error("Failed to load room types", err);
            setError(err?.message || 'Room types could not be loaded.');
        } finally {
            setLoading(false);
        }
    };

    const handleCreate = () => {
        setSelectedRoomType(null);
        setShowModal(true);
    };

    const handleEdit = (roomType) => {
        setSelectedRoomType(roomType);
        setShowModal(true);
    };

    const handleSave = async (data) => {
        try {
            if (selectedRoomType?.id && !selectedRoomType.id.startsWith('fallback-')) {
                await roomService.updateRoomType(selectedRoomType.id, data);
                setNotice({ type: 'success', message: `${data.name} was updated successfully.` });
            } else {
                await roomService.createRoomType(data);
                setNotice({ type: 'success', message: `${data.name} was created successfully.` });
            }
            await fetchRoomTypes();
            setShowModal(false);
        } catch (saveError) {
            setNotice({ type: 'error', message: saveError?.message || 'The room type could not be saved.' });
            throw saveError;
        }
    };

    const handleDelete = async (roomType) => {
        if (roomType.id.startsWith('fallback-')) {
            setNotice({ type: 'error', message: 'Cannot delete a built-in room type.' });
            return;
        }
        if (window.confirm(`Are you sure you want to delete ${roomType.name}?`)) {
            try {
                await roomService.deleteRoomType(roomType.id);
                setNotice({ type: 'success', message: 'Room type deleted successfully.' });
                fetchRoomTypes();
            } catch (err) {
                setNotice({ type: 'error', message: err?.message || 'Failed to delete room type.' });
            }
        }
    };

    return (
        <div className="space-y-6">
            <ActionToast notice={notice} onClose={() => setNotice(null)} />
            <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between space-y-4 lg:space-y-0">
                <div>
                    <h3 className="text-lg font-semibold text-foreground">Room Types</h3>
                    <p className="text-sm text-muted-foreground">Manage the catalogue of room types used for scheduling and capacity planning.</p>
                </div>
                <div className="flex items-center space-x-2">
                    <Button variant="default" size="sm" onClick={handleCreate}>
                        <Icon name="Plus" size={14} className="mr-1" />
                        Add Room Type
                    </Button>
                </div>
            </div>

            {error && (
                <div className="rounded-lg border border-red-200 bg-red-50 p-4 text-red-800">
                    <p>{error}</p>
                    <Button variant="outline" size="sm" className="mt-3" onClick={fetchRoomTypes}>
                        <Icon name="RefreshCw" size={14} className="mr-2" />Retry
                    </Button>
                </div>
            )}

            {!error && (
                <div className="flex-1 rounded-md border border-blue-200 overflow-hidden flex flex-col bg-background shadow-sm h-[calc(100vh-280px)]">
                    <div className="overflow-y-auto flex-1">
                        <table className="w-full text-sm">
                            <thead className="bg-white text-muted-foreground sticky top-0 z-10 shadow-sm">
                                <tr>
                                    <th className="px-4 py-3 text-left font-medium border-b-2 border-blue-100">Code</th>
                                    <th className="px-4 py-3 text-left font-medium border-b-2 border-blue-100">Name</th>
                                    <th className="px-4 py-3 text-left font-medium border-b-2 border-blue-100">Category</th>
                                    <th className="px-4 py-3 text-left font-medium border-b-2 border-blue-100">Specialized Space</th>
                                    <th className="px-4 py-3 text-left font-medium border-b-2 border-blue-100">Status</th>
                                    <th className="px-4 py-3 text-right font-medium border-b-2 border-blue-100">Actions</th>
                                </tr>
                            </thead>
                            <tbody className="divide-y divide-blue-100">
                                {loading && <tr><td colSpan="6" className="px-4 py-12 text-center text-muted-foreground"><Icon name="Loader" size={24} className="mx-auto mb-2 animate-spin" />Loading room types…</td></tr>}
                                {!loading && roomTypes.map((type) => {
                                    const isFallback = String(type.id).startsWith('fallback-');
                                    return (
                                        <tr key={type.id || type.name} className="hover:bg-blue-50/50 transition-colors group">
                                            <td className="px-4 py-3 border-b border-blue-100 font-mono text-xs text-blue-800">{type.code || 'N/A'}</td>
                                            <td className="px-4 py-3 border-b border-blue-100 font-medium text-gray-800">{type.name}</td>
                                            <td className="px-4 py-3 border-b border-blue-100 text-gray-600">{type.category}</td>
                                            <td className="px-4 py-3 border-b border-blue-100 text-gray-700">
                                                {type.requires_specialized_space ? 'Yes' : 'No'}
                                            </td>
                                            <td className="px-4 py-3 border-b border-blue-100">
                                                <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${type.is_active !== false ? 'bg-blue-100 text-blue-700' : 'bg-gray-100 text-gray-600'}`}>
                                                    {type.is_active !== false ? 'Active' : 'Inactive'}
                                                </span>
                                            </td>
                                            <td className="px-4 py-3 border-b border-blue-100 text-right">
                                                <div className="flex items-center justify-end space-x-2 opacity-0 group-hover:opacity-100 transition-opacity">
                                                    <Button variant="ghost" size="icon" onClick={() => handleEdit(type)} title="Edit">
                                                        <Icon name="Edit" size={14} className="text-muted-foreground hover:text-foreground" />
                                                    </Button>
                                                    {!isFallback && (
                                                        <Button variant="ghost" size="icon" className="text-error hover:text-error hover:bg-error/10" onClick={() => handleDelete(type)} title="Delete">
                                                            <Icon name="Trash2" size={14} />
                                                        </Button>
                                                    )}
                                                </div>
                                            </td>
                                        </tr>
                                    );
                                })}
                                {!loading && roomTypes.length === 0 && (
                                    <tr>
                                        <td colSpan="6" className="px-4 py-12 text-center text-muted-foreground">
                                            No room types found.
                                        </td>
                                    </tr>
                                )}
                            </tbody>
                        </table>
                    </div>
                </div>
            )}
            {showModal && (
                <RoomTypeModal
                    isOpen={showModal}
                    onClose={() => setShowModal(false)}
                    onSave={handleSave}
                    roomType={selectedRoomType}
                />
            )}
        </div>
    );
};

export default RoomTypeList;
